import { createSlice } from '@reduxjs/toolkit';
import { Checkout, DeliveryOptionSlug } from '../../domain/checkout/checkout.model';
import {
  GoToCheckoutAction,
  SetDeliveryInfoAction,
  SetDeliveryOptionAction,
  SetGuessDataAction,
} from './checkout.types';
import { createOrderAction } from './checkout.thunks';
import container from '../../container';
import { AnalyticsAdapter, AnalyticsEventCategory, AnalyticsEventName } from '../../domain/analytics/AnalyticsAdapter';

export interface CheckoutState extends Checkout {
  isLoading: boolean;
}

export const getDeliveryAmount = (value: DeliveryOptionSlug): number => {
  switch (value) {
    case DeliveryOptionSlug.REGULAR:
      return 5;
    case DeliveryOptionSlug.INTERNATIONAL:
      return 6;
    case DeliveryOptionSlug.EXPRESS:
      return 9;
    default:
      return 0;
  }
};

export const initialState: CheckoutState = {
  cartId: '',
  products: [],
  discount: 0,
  subTotal: 0,
  total: 0,
  deliveryInfo: {
    address: '',
    zipCode: '',
    city: '',
    country: '',
    phone: '',
  },
  deliveryOption: {
    type: DeliveryOptionSlug.REGULAR,
    amount: getDeliveryAmount(DeliveryOptionSlug.REGULAR),
  },
  isGuess: false,
  guessData: undefined,
  isLoading: false,
};

const analyticsAdapter = container.get<AnalyticsAdapter>('AnalyticsAdapter');

export const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    setInitialCheckoutState(state, action: GoToCheckoutAction) {
      const { cartId, products, discount, subTotal, total, isUser } = action.payload;
      state.cartId = cartId;
      state.products = products;
      state.discount = discount;
      state.subTotal = subTotal;
      state.total = total;
      if (isUser) {
        state.guessData = undefined;
        state.isGuess = false;
      }
    },
    setGuessData(state, action: SetGuessDataAction) {
      state.guessData = action.payload;
      state.isGuess = true;
      analyticsAdapter.trackEvent({
        action: AnalyticsEventName.CHECKOUT_STEP_1,
        category: AnalyticsEventCategory.ECOMMERCE,
        params: {
          user_email: action.payload.email,
          is_guess: true,
        },
      });
    },
    setDeliveryInfo(state, action: SetDeliveryInfoAction) {
      const { address, zipCode, city, country, phone } = action.payload;
      analyticsAdapter.trackEvent({
        action: AnalyticsEventName.CHECKOUT_STEP_2,
        category: AnalyticsEventCategory.ECOMMERCE,
        params: {
          delivery_address: address,
          delivery_zipCode: zipCode,
          delivery_city: city,
          delivery_country: country,
          delivery_phone: phone,
        },
      });
      state.deliveryInfo = {
        address,
        zipCode,
        city,
        country,
        phone,
      };
    },

    setDeliveryOption(state, action: SetDeliveryOptionAction) {
      const { type, amount } = action.payload;
      analyticsAdapter.trackEvent({
        action: AnalyticsEventName.CHECKOUT_STEP_3,
        category: AnalyticsEventCategory.ECOMMERCE,
        params: {
          delivery_option_type: type,
          delivery_option_amount: amount,
        },
      });
      state.deliveryOption = {
        type,
        amount,
      };
    },
    calculateTotal(state) {
      state.total = state.subTotal + state.deliveryOption.amount - state.discount;
    },
  },
  extraReducers(builder) {
    builder.addCase(createOrderAction.pending, (state, _) => {
      state.isLoading = true;
    });
    builder.addCase(createOrderAction.fulfilled, (state, action) => {
      state.isLoading = false;
      state.orderId = action.payload;
      analyticsAdapter.trackEvent({
        action: AnalyticsEventName.CHECKOUT_STEP_4,
        category: AnalyticsEventCategory.ECOMMERCE,
        params: {
          order_id: action.payload,
        },
      });
    });
    builder.addCase(createOrderAction.rejected, (state, _) => {
      state.isLoading = false;
    });
  },
});

export const { setInitialCheckoutState, setGuessData, setDeliveryInfo, setDeliveryOption, calculateTotal } =
  checkoutSlice.actions;
export default checkoutSlice.reducer;
